import { useState } from "react";
import {
    ChevronLeft,
    Copy,
    Check,
    Eye,
    EyeOff,
    Pencil,
    Trash2,
} from "lucide-react";
import {
    C,
    CRED_TYPES,
    sBadge,
    sFull,
    sTopBar,
    expColor,
    expLabel,
    fmtDate,
} from "../styles";

const SECRET_FIELDS = new Set(["Password", "Key", "Connection URL", "Content"]);

export default function EntryDetail({
    entry,
    onEdit,
    onBack,
    onDelete,
    showToast,
}) {
    const type = CRED_TYPES.find((t) => t.id === entry.type) || CRED_TYPES[0];
    const [copied, setCopied] = useState("");
    const [revealed, setRevealed] = useState({});

    const copyValue = async (label, value) => {
        if (!value) return;
        await navigator.clipboard?.writeText(value);
        setCopied(label);
        setTimeout(() => setCopied(""), 1800);
        // Auto-clear clipboard after 30 seconds for sensitive fields
        if (SECRET_FIELDS.has(label)) {
            setTimeout(() => {
                navigator.clipboard?.writeText("").catch(() => {});
            }, 30000);
            showToast && showToast(`${label} copied · clears in 30s`);
        } else {
            showToast && showToast(`${label} copied`);
        }
    };

    return (
        <div style={sFull} data-testid="detail-screen">
            <div style={sTopBar}>
                <button onClick={onBack} style={iconBtn} data-testid="detail-back-btn">
                    <ChevronLeft size={16} />
                </button>
                <div
                    style={{
                        fontSize: 12,
                        color: C.muted,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                    }}
                >
                    {type.label}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    <button
                        onClick={onEdit}
                        style={iconBtn}
                        data-testid="detail-edit-btn"
                    >
                        <Pencil size={14} />
                    </button>
                    <button
                        onClick={onDelete}
                        style={{
                            ...iconBtn,
                            color: C.danger,
                            borderColor: "rgba(217,43,43,0.3)",
                        }}
                        data-testid="detail-delete-btn"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>

            <div
                style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "22px 20px 40px",
                    maxWidth: 560,
                    width: "100%",
                    margin: "0 auto",
                }}
            >
                <div
                    style={{
                        fontSize: 22,
                        color: C.text,
                        fontWeight: 400,
                        marginBottom: 6,
                    }}
                >
                    {entry.fields[type.fields[0]] || "(untitled)"}
                </div>
                <div
                    style={{
                        fontSize: 11,
                        color: C.dim,
                        letterSpacing: "0.1em",
                        marginBottom: 24,
                    }}
                >
                    UPDATED {fmtDate(entry.updatedAt).toUpperCase()}
                    {entry.fields?.Expiry && (
                        <>
                            {"  ·  "}
                            <span style={sBadge(expColor(entry.fields.Expiry))}>
                                {expLabel(entry.fields.Expiry)}
                            </span>
                        </>
                    )}
                </div>

                {type.fields.map((fld) => {
                    const val = entry.fields[fld];
                    if (!val && fld !== type.fields[0]) return null;
                    const isSecret = SECRET_FIELDS.has(fld);
                    const isRevealed = revealed[fld];
                    return (
                        <div key={fld} style={fieldRow}>
                            <div style={fieldLabel}>{fld.toUpperCase()}</div>
                            <div style={fieldValue}>
                                {fld === "Content" ? (
                                    <pre
                                        style={{
                                            fontFamily: C.font,
                                            fontSize: 13,
                                            color: C.text,
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                            margin: 0,
                                            padding: 0,
                                        }}
                                    >
                                        {val || "—"}
                                    </pre>
                                ) : fld === "Expiry" ? (
                                    <span style={{ color: C.text }}>
                                        {fmtDate(val)}
                                    </span>
                                ) : (
                                    <span
                                        style={{
                                            color: C.text,
                                            wordBreak: "break-all",
                                            fontFamily: C.font,
                                            fontSize: 13,
                                        }}
                                    >
                                        {isSecret && !isRevealed
                                            ? "•".repeat(Math.min(val.length, 14))
                                            : val}
                                    </span>
                                )}
                            </div>
                            <div style={fieldActions}>
                                {isSecret && val && (
                                    <button
                                        onClick={() =>
                                            setRevealed({
                                                ...revealed,
                                                [fld]: !isRevealed,
                                            })
                                        }
                                        style={smallBtn}
                                        title={isRevealed ? "Hide" : "Reveal"}
                                        data-testid={`detail-reveal-${fld}`}
                                    >
                                        {isRevealed ? (
                                            <EyeOff size={13} />
                                        ) : (
                                            <Eye size={13} />
                                        )}
                                    </button>
                                )}
                                {val && fld !== "Content" && fld !== "Expiry" && (
                                    <button
                                        onClick={() => copyValue(fld, val)}
                                        style={smallBtn}
                                        title="Copy"
                                        data-testid={`detail-copy-${fld}`}
                                    >
                                        {copied === fld ? (
                                            <Check size={13} color={C.accent} />
                                        ) : (
                                            <Copy size={13} />
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

const iconBtn = {
    background: "transparent",
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    color: C.muted,
    padding: "7px 12px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    fontFamily: C.font,
    fontSize: 12,
};

const fieldRow = {
    display: "grid",
    gridTemplateColumns: "100px 1fr auto",
    gap: 14,
    alignItems: "flex-start",
    padding: "14px 0",
    borderTop: `1px solid ${C.border}`,
};

const fieldLabel = {
    fontSize: 10,
    letterSpacing: "0.2em",
    color: C.dim,
    marginTop: 2,
};

const fieldValue = {
    minWidth: 0,
};

const fieldActions = {
    display: "flex",
    gap: 6,
    flexShrink: 0,
};

const smallBtn = {
    background: "transparent",
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    color: C.muted,
    padding: "6px 8px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
};
